import React, { useEffect, useState } from 'react';

import { Badge, Col, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useHistory } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import {
  backgroundGrey5,
  blackberry,
  coolGrey,
  grape,
  greyHover,
  greyStroke,
  modernSalmon,
  modernSalmonBackground,
  positiveGreen,
  purple1,
  softAmber,
  softAmberLight,
  softGreenLight,
  softPurple,
  softPurpleLight,
  white,
} from 'lib/css/colors';
import { BRAND_SKU_MAPPER_MANUFACTURERS } from 'lib/networking/endpoints';
import {
  BRAND_SKU_MAPPER_SCOPE_TABS,
  BrandSkuMapperRunStatus,
  BrandSkuMapperScope,
} from 'lib/enums/BrandSkuMapperEnums';
import Container from 'components/shared/Container';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import SearchBar from 'components/shared/SearchBar';
import TabBar from 'components/shared/TabBar';
import useGet from 'lib/hooks/useGet';

const DEFAULT_PAGE_SIZE = 20;

function BrandSkuMapperManufacturersView() {
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery] = useDebounce(searchInput, 500);
  const [scope, setScope] = useState(BrandSkuMapperScope.REBATE);

  useEffect(() => {
    setCurrentPage(1);
  }, [scope]);

  const isAllBrandsTab = scope === BrandSkuMapperScope.ALL_GMV_PRODUCTS;

  const handleAllBrandsTabSelect = () => {
    history.push('/enrich-products/brand-sku-mapper/all-gmv-products');
  };

  const params = {
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
  };
  if (searchQuery) {
    params.query = searchQuery;
  }
  if (scope === BrandSkuMapperScope.REBATE) {
    params.rebate_only = true;
  }

  // Skip the manufacturers API call when the ALL_GMV_PRODUCTS tab is selected — we
  // navigate away immediately, so there is nothing to render.
  const { data, loading } = useGet(BRAND_SKU_MAPPER_MANUFACTURERS, params, isAllBrandsTab);

  const manufacturers = data?.manufacturers || [];
  const total = data?.count || 0;

  const renderStatus = run => {
    if (!run) {
      return <span className={css(styles.mutedText)}>Not analyzed yet</span>;
    }
    const status = run.status;
    let bg = softPurpleLight;
    let color = softPurple;
    if (status === BrandSkuMapperRunStatus.COMPLETED) {
      bg = softGreenLight;
      color = positiveGreen;
    } else if (
      status === BrandSkuMapperRunStatus.RUNNING ||
      status === BrandSkuMapperRunStatus.PENDING
    ) {
      bg = softAmberLight;
      color = softAmber;
    } else if (status === BrandSkuMapperRunStatus.FAILED) {
      bg = modernSalmonBackground;
      color = modernSalmon;
    }
    return (
      <span className={css(styles.statusPill)} style={{ backgroundColor: bg, color: color }}>
        {status}
      </span>
    );
  };

  return (
    <div className={css(styles.pageContainer)}>
      <div className={css(styles.headerSection)}>
        <div className={css(styles.headerContent)}>
          <h1 className={css(styles.pageTitle)}>Brand SKU Mapper</h1>
          <div className={css(styles.headerSubtitle)}>
            Map unmapped branded vendor products to manufacturer products using image-based AI
          </div>
        </div>
      </div>

      <Container className={css(styles.mainContent)}>
        <TabBar
          tabs={BRAND_SKU_MAPPER_SCOPE_TABS}
          activeKey={scope}
          onSelect={key => {
            if (key === BrandSkuMapperScope.ALL_GMV_PRODUCTS) {
              setScope(key);
              handleAllBrandsTabSelect();
              return;
            }
            setScope(key);
            setCurrentPage(1);
          }}
        />

        <Row className={css(styles.toolbarRow)}>
          <Col xs={5} className="d-flex align-items-center">
            <div className={css(styles.searchWrapper)}>
              <SearchBar
                value={searchInput}
                setValue={setSearchInput}
                placeholder="Search manufacturer by name"
              />
            </div>
          </Col>
          <Col xs={7} className={css(styles.countCol)}>
            <Badge variant="light" className={css(styles.countBadge)}>
              {searchQuery
                ? `${total.toLocaleString()} matching`
                : `Top ${Math.min(
                    itemsPerPage,
                    total,
                  ).toLocaleString()} of ${total.toLocaleString()}`}
            </Badge>
          </Col>
        </Row>
        <Row>
          <Col className="d-flex mt-3">
            <span className={classNames(css(styles.selectedText), 'text-secondary ml-2')}>
              {total.toLocaleString()} manufacturer{total === 1 ? '' : 's'} found
            </span>
          </Col>
          <Col className={classNames(css(styles.pagination), 'd-flex justify-content-end')}>
            <PaginationComponent
              totalItems={total}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              defaultSize
            />
          </Col>
        </Row>
        <div className={css(styles.scrollableContainer)}>
          {loading ? (
            <LoadingSpinner short />
          ) : (
            <Table hover className={css(styles.table)}>
              <thead className={css(styles.tableHeader)}>
                <tr>
                  <th className={css(styles.rankCol)}>Rank</th>
                  <th>Manufacturer</th>
                  <th className="text-right">Verified manufacturer products</th>
                  <th>Latest run</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {manufacturers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted p-4">
                      No manufacturers found.
                    </td>
                  </tr>
                ) : (
                  manufacturers.map((m, idx) => {
                    const rank = itemsPerPage * (currentPage - 1) + idx + 1;
                    return (
                      <tr key={m.id} className={css(styles.row)}>
                        <td className="align-middle">
                          <div className={css(styles.rankNumber)}>#{rank}</div>
                        </td>
                        <td className="align-middle">
                          <div className="d-flex align-items-center">
                            {m.logo_url ? (
                              <img src={m.logo_url} alt={m.name} className={css(styles.logo)} />
                            ) : (
                              <div className={css(styles.logoPlaceholder)}>
                                {m.name?.[0] || '?'}
                              </div>
                            )}
                            <div>
                              <div className={css(styles.mfrName)}>{m.name}</div>
                              <div className={css(styles.mutedText)}>#{m.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-right align-middle">
                          <Badge
                            className={css(
                              styles.countPill,
                              (m.verified_mfr_product_count || 0) === 0 && styles.countPillZero,
                            )}
                          >
                            {(m.verified_mfr_product_count || 0).toLocaleString()}
                          </Badge>
                        </td>
                        <td className="align-middle">
                          {renderStatus(m.latest_run)}
                          {m.latest_run && (
                            <div className={css(styles.mutedText)}>
                              {`${m.latest_run.matched_count || 0}/${
                                m.latest_run.total_vendor_products || 0
                              } matched`}
                            </div>
                          )}
                        </td>
                        <td className="align-middle text-right">
                          <OverlayTrigger
                            placement="left"
                            overlay={<Tooltip id={`open-mfr-${m.id}`}>Open manufacturer</Tooltip>}
                          >
                            <Link
                              to={`/enrich-products/brand-sku-mapper/manufacturers/${m.id}`}
                              aria-label={`Open ${m.name}`}
                              className={css(styles.openLink)}
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </Link>
                          </OverlayTrigger>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          )}
        </div>
      </Container>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: backgroundGrey5,
  },
  headerSection: {
    background: `linear-gradient(135deg, ${blackberry} 0%, ${purple1} 100%)`,
    padding: '24px 40px',
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 1600,
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 600,
    color: white,
    margin: 0,
  },
  headerSubtitle: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 14,
    marginTop: 4,
  },
  mainContent: {
    padding: '24px 40px',
  },
  toolbarRow: {
    marginBottom: 16,
    alignItems: 'center',
  },
  searchWrapper: {
    flex: 1,
    maxWidth: 300,
  },
  countCol: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  countBadge: {
    color: blackberry,
    backgroundColor: softPurpleLight,
    fontSize: 13,
    padding: '8px 12px',
    fontWeight: 600,
  },
  selectedText: {
    fontSize: '1rem',
    color: coolGrey,
  },
  pagination: {
    marginTop: '1rem',
    zIndex: 2,
  },
  scrollableContainer: {
    height: '70vh',
    backgroundColor: white,
    borderRadius: 12,
    border: `1px solid ${greyStroke}`,
    overflow: 'auto',
  },
  table: {
    marginBottom: 0,
  },
  tableHeader: {
    backgroundColor: softPurpleLight,
    color: blackberry,
  },
  rankCol: {
    width: 90,
  },
  row: {
    ':hover': {
      backgroundColor: softPurpleLight,
    },
  },
  rankNumber: {
    color: coolGrey,
    fontWeight: 600,
    fontSize: 14,
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 8,
    marginRight: 12,
    objectFit: 'contain',
    backgroundColor: white,
    border: `1px solid ${greyStroke}`,
  },
  logoPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 8,
    marginRight: 12,
    backgroundColor: softPurpleLight,
    color: blackberry,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
  },
  mfrName: {
    color: blackberry,
    fontWeight: 600,
  },
  mutedText: {
    color: coolGrey,
    fontSize: 12,
  },
  countPill: {
    backgroundColor: softPurpleLight,
    color: blackberry,
    fontSize: 15,
    fontWeight: 700,
    padding: '8px 14px',
    borderRadius: 999,
  },
  countPillZero: {
    backgroundColor: greyHover,
    color: coolGrey,
  },
  statusPill: {
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  openLink: {
    color: grape,
    fontSize: 18,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 8,
    ':hover': {
      color: white,
      backgroundColor: grape,
      textDecoration: 'none',
    },
  },
});

export default BrandSkuMapperManufacturersView;
